import { create } from 'zustand';

/**
 * Flat, export-ready shape for a student sitting in the export cart.
 * Intentionally identical to `StudentExportData` in ExportStudentsZip.tsx —
 * items pulled from the cart are fed straight into the export dialog
 * without another mapping step.
 */
export interface ExportCartStudent {
  id: string;
  sp_student_id: string;
  user_id: string;
  name: string;
  father_name: string;
  mother_name: string;
  address: string;
  father_no: string;
  mother_no: string;
  gender: string;
  profile_picture_url: string;
  admission_no: string;
  class_id: string;
  class_name: string;
  section: string | null;
  roll_no: number;
  email_id: string;
  dob: string;
  blood_group: string;
  admission_type: string | null;
  user_login?: string;
}

interface ExportCartState {
  /** Keyed by student.id so re-marking the same student is a no-op update, not a dupe. */
  items: Record<string, ExportCartStudent>;
  count: number;
  addMany: (students: ExportCartStudent[]) => void;
  remove: (id: string) => void;
  toggle: (student: ExportCartStudent) => void;
  clear: () => void;
  isMarked: (id: string) => boolean;
}

export const useExportCartStore = create<ExportCartState>((set, get) => ({
  items: {},
  count: 0,

  addMany: (students) =>
    set((state) => {
      const next = { ...state.items };
      students.forEach((s) => {
        next[s.id] = s;
      });
      return { items: next, count: Object.keys(next).length };
    }),

  remove: (id) =>
    set((state) => {
      if (!state.items[id]) return state;
      const next = { ...state.items };
      delete next[id];
      return { items: next, count: Object.keys(next).length };
    }),

  toggle: (student) =>
    set((state) => {
      const next = { ...state.items };
      if (next[student.id]) {
        delete next[student.id];
      } else {
        next[student.id] = student;
      }
      return { items: next, count: Object.keys(next).length };
    }),

  clear: () => set({ items: {}, count: 0 }),

  isMarked: (id) => !!get().items[id],
}));

/**
 * Maps a dashboard `StudentData` row (new architecture — flat
 * `profile_picture_url`, `sp_student_id` present) into the cart/export
 * shape. Kept as a pure function (not store state) so the dashboard can
 * call it inline without importing extra types.
 */
export function toExportCartStudent(s: {
  id: string;
  sp_student_id: string;
  user_id: string;
  name: string;
  father_name: string;
  mother_name: string;
  address: string;
  father_no: string;
  mother_no: string;
  gender: string;
  profile_picture_url: string;
  admission_no: string;
  class_id: string;
  class_name: string;
  section: string | null;
  roll_no: number;
  email_id: string;
  dob: string;
  blood_group: string;
  admission_type: string | null;
}): ExportCartStudent {
  return {
    id: s.id,
    sp_student_id: s.sp_student_id,
    user_id: s.user_id,
    name: s.name,
    father_name: s.father_name,
    mother_name: s.mother_name,
    address: s.address,
    father_no: s.father_no,
    mother_no: s.mother_no,
    gender: s.gender,
    profile_picture_url: s.profile_picture_url,
    admission_no: s.admission_no,
    class_id: s.class_id,
    class_name: s.class_name,
    section: s.section,
    roll_no: s.roll_no,
    email_id: s.email_id,
    dob: s.dob,
    blood_group: s.blood_group,
    admission_type: s.admission_type,
  };
}
